import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ChevronRight,
  ArrowRight,
  Sparkles,
  Check,
  TrendingUp,
  Users,
  Target,
  ShieldAlert,
  MessageCircle,
  Zap,
  Lightbulb,
  Star,
} from "lucide-react";
import validatorApi from "../services/validatorApi";
import EnhancedFooter from "./Footer";
import IframeResizer from "iframe-resizer-react";
import { motion, AnimatePresence } from "framer-motion";

const delayedFadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.8, delay: 1.2 } },
};

// Toggle between mock data and real API
const USE_REAL_API = true; // Set to true in production

const FreeIdeaValidator = () => {
  const navigate = useNavigate();
  const [idea, setIdea] = useState("");
  const [industry, setIndustry] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [error, setError] = useState(null);

  // SEO keywords and content variables
  const pageTitle = "Free Startup Idea Validator | Test Market Demand in 60 Seconds | No Login Required";
  const pageDescription =
    "Validate your startup idea for free in 60 seconds. Get real market data, competitor analysis, and sentiment scores before you build. Save time and money.";
  const pageKeywords =
    "startup idea validator, validate business idea, startup validation, business idea testing, market demand analysis, startup tool, free startup validation";
  const canonicalUrl = "https://feedbackbyai.com/tools/free-startup-idea-validator/";

  // Progress animation during analysis
  useEffect(() => {
    let interval;
    if (isAnalyzing) {
      interval = setInterval(() => {
        setAnalysisProgress((prev) => {
          if (prev >= 95) {
            clearInterval(interval);
            return 95;
          }
          return prev + Math.random() * 3;
        });
      }, 100);
    }
    return () => clearInterval(interval);
  }, [isAnalyzing]);

  // Direct DOM manipulation for meta tags
  useEffect(() => {
    // Update meta tags to override the default ones
    const updateMetaTag = (selector, attribute, value) => {
      let element = document.querySelector(selector);
      if (element) {
        element.setAttribute(attribute, value);
      } else {
        element = document.createElement("meta");
        if (selector.includes("property=")) {
          element.setAttribute("property", selector.split("property=")[1].replace(/['"]/g, ""));
        } else if (selector.includes("name=")) {
          element.setAttribute("name", selector.split("name=")[1].replace(/['"]/g, ""));
        }
        element.setAttribute(attribute, value);
        document.head.appendChild(element);
      }
    };

    // Title and basic meta tags
    document.title = pageTitle;

    updateMetaTag('meta[name="description"]', "content", pageDescription);
    updateMetaTag('meta[name="keywords"]', "content", pageKeywords);

    // OpenGraph tags
    updateMetaTag('meta[property="og:title"]', "content", pageTitle);
    updateMetaTag('meta[property="og:description"]', "content", pageDescription);
    updateMetaTag('meta[property="og:url"]', "content", canonicalUrl);
    updateMetaTag('meta[property="og:type"]', "content", "website");
    updateMetaTag('meta[property="og:image"]', "content", "https://feedbackbyai.com/thumbnail.png");

    // Twitter Card tags
    updateMetaTag('meta[name="twitter:card"]', "content", "summary_large_image");
    updateMetaTag('meta[name="twitter:title"]', "content", pageTitle);
    updateMetaTag('meta[name="twitter:description"]', "content", pageDescription);
    updateMetaTag('meta[name="twitter:image"]', "content", "https://feedbackbyai.com/thumbnail.png");

    // Add canonical URL
    let canonicalElement = document.querySelector('link[rel="canonical"]');
    if (!canonicalElement) {
      canonicalElement = document.createElement("link");
      canonicalElement.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalElement);
    }
    canonicalElement.setAttribute("href", canonicalUrl);
  }, []);

  const handleSubmit = async () => {
    if (!idea.trim()) return;
    setIsAnalyzing(true);
    setAnalysisProgress(0);
    setError(null);

    try {
      const response = await validatorApi.validateIdea(idea, industry);
      if (response.success) {
        setIsAnalyzing(false);
        navigate(`/ideavalidatorresult/${response.data.id}`);
      } else {
        // Handle rate limit specifically
        if (response.rateLimit) {
          setError(
            <div>
              <p>
                <strong>Rate limit exceeded</strong>
              </p>
              <p>You've reached the maximum number of validations allowed. Please upgrade to our premium plan for higher limits.</p>
            </div>
          );
        } else {
          setError(response.error || "Failed to validate your idea. Please try again.");
        }
        setIsAnalyzing(false);
      }
    } catch (err) {
      console.error("Error validating idea:", err);
      setError("An unexpected error occurred. Please try again.");
      setIsAnalyzing(false);
    }
  };

  const sampleReport = {
    demandScore: 72,
    sources: ["r/startups", "r/fitness", "r/freelance"],
    marketSignals: [
      '"Nutrition coaching app" was mentioned 142+ times in the last 3 months.',
      "Tools like Cronometer and MyFitnessPal have active community interest.",
      "Top unmet need: Personalized plan creation and remote accountability.",
    ],
    competitors: ["Cronometer", "BetterMe", "NutriCheck"],
    risks: ["Users trust influencers more than apps in this space.", "Retention is hard without social features."],
    recommendedAngle: "Make it feel like having a personal coach, not just a tracker.",
    sentiment: [
      "Need a simple way to get nutrition plans — sick of apps that feel like calorie spreadsheets.",
      "Why can't a coach just message me directly and give me what to eat?",
    ],
  };

  const renderSampleReport = () => {
    return (
      <div className="font-mono text-sm">
        <div className="flex items-center gap-2 font-semibold text-emerald-700 mb-4">
          <Check className="h-5 w-5" />
          <span>Startup Validation Report — v0.1</span>
        </div>
        <div className="mb-4">
          <div className="flex items-center gap-2 font-semibold">
            <TrendingUp className="h-4 w-4 text-emerald-600" />
            <span>Demand Score: {sampleReport.demandScore}/100</span>
          </div>
          <div className="text-gray-600 ml-6">→ Based on recent Reddit threads in {sampleReport.sources.join(", ")}.</div>
        </div>
        <div className="mb-4">
          <div className="flex items-start gap-2 font-semibold">
            <Zap className="h-4 w-4 flex-shrink-0 text-emerald-600 mt-1" />
            <span>Market Signals:</span>
          </div>
          <ul className="ml-6 space-y-1 mt-1">
            {sampleReport.marketSignals.map((signal, idx) => (
              <li key={idx} className="flex items-start">
                <span className="mr-2 text-emerald-700">•</span>
                <span>{signal}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="mb-4">
          <div className="flex items-start gap-2 font-semibold">
            <Users className="h-4 w-4 flex-shrink-0 text-emerald-600 mt-1" />
            <span>Top Overlapping Competitors:</span>
          </div>
          <ul className="ml-6 space-y-1 mt-1">
            {sampleReport.competitors.map((competitor, idx) => (
              <li key={idx} className="flex items-start">
                <span className="mr-2 text-emerald-700">•</span>
                <span>{competitor}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="mb-4">
          <div className="flex items-start gap-2 font-semibold">
            <ShieldAlert className="h-4 w-4 flex-shrink-0 text-emerald-600 mt-1" />
            <span>Risks:</span>
          </div>
          <ul className="ml-6 space-y-1 mt-1">
            {sampleReport.risks.map((risk, idx) => (
              <li key={idx} className="flex items-start">
                <span className="mr-2 text-emerald-700">•</span>
                <span>{risk}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="mb-4">
          <div className="flex items-start gap-2 font-semibold">
            <Target className="h-4 w-4 flex-shrink-0 text-emerald-600 mt-1" />
            <span>Recommended Angle:</span>
          </div>
          <div className="ml-6 mt-1 italic text-emerald-800 font-medium">"{sampleReport.recommendedAngle}"</div>
        </div>
        <div className="mb-4">
          <div className="flex items-start gap-2 font-semibold">
            <MessageCircle className="h-4 w-4 flex-shrink-0 text-emerald-600 mt-1" />
            <span>Reddit Sentiment:</span>
          </div>
          <ul className="ml-6 space-y-1 mt-1">
            {sampleReport.sentiment.map((quote, idx) => (
              <li key={idx} className="flex items-start">
                <span className="mr-2 text-emerald-700">•</span>
                <span>"{quote}"</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="border-t border-dashed border-gray-200 my-4 pt-4"></div>
        <div className="text-center">
          <p className="font-semibold text-gray-800 mb-2">👋 Want a full GTM plan + investor-ready report?</p>
          <Link
            to="/dynamicreports/"
            className="mt-2 inline-flex items-center text-emerald-600 font-medium hover:text-emerald-700 transition-colors"
          >
            Try the Full Business Planner <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 font-sans">
      {/* Helmet for SEO - this is a belt-and-suspenders approach with the direct DOM manipulation above */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={pageKeywords} />
        <link rel="canonical" href={canonicalUrl} />

        {/* OpenGraph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content="https://feedbackbyai.com/validator-preview.jpg" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content="https://feedbackbyai.com/validator-preview.jpg" />

        {/* Schema.org structured data for Rich Snippets */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: "Free Startup Idea Validator",
            url: canonicalUrl,
            description: pageDescription,
            applicationCategory: "BusinessApplication",
            operatingSystem: "All",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
            },
            publisher: {
              "@type": "Organization",
              name: "FeedbackbyAI",
              logo: {
                "@type": "ImageObject",
                url: "https://feedbackbyai.com/180x180.png",
              },
            },
            screenshot: {
              "@type": "ImageObject",
              url: "https://feedbackbyai.com/validator-preview.jpg",
            },
          })}
        </script>
      </Helmet>

      {/* Navigation */}
      <nav className="fixed top-0 inset-x-0 z-50 backdrop-blur-lg bg-white/80 border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <div className="text-sm font-medium">
                <Link to="/" className="flex items-center" style={{ textDecoration: "none", color: "inherit" }}>
                  <div className="inline-flex items-center justify-center h-8 w-8 rounded-lg mr-2 bg-gradient-to-br from-emerald-500 to-teal-500">
                    <Sparkles className="h-4 w-4 text-white" />
                  </div>
                  <span className="font-semibold text-base">FeedbackbyAI</span>
                </Link>
              </div>
            </div>
            <div className="hidden md:flex space-x-8 items-center">
              <a href="/ideate" className="text-sm text-gray-500 hover:text-emerald-700 transition-colors">
                Brainstorm Idea
              </a>
              <a href="/dynamicreports/" className="text-sm text-gray-500 hover:text-emerald-700 transition-colors">
                100+ Page Report
              </a>
              <a href="/leadfinder" className="text-sm text-gray-500 hover:text-emerald-700 transition-colors">
                Buyer Intent Search
              </a>
              <a href="/video" className="text-sm text-gray-500 hover:text-emerald-700 transition-colors">
                Automate TikTok & Reels
              </a>

              <a href="/signin" className="text-sm px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-900 transition-all">
                Sign in
              </a>
              <a
                href="/"
                className="text-sm px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white transition-all flex items-center"
              >
                Full Report <ArrowRight className="ml-1 h-3 w-3" />
              </a>
            </div>
            <div className="md:hidden flex items-center">
              <button className="text-gray-500 hover:text-gray-700 focus:outline-none">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-20 pb-16">
        {/* Hero Section */}
        <section className="pt-10 pb-16 sm:pt-14 sm:pb-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-block mb-6">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700">
                  Trusted by 10,000+ founders
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-900 mb-6 bg-clip-text text-transparent bg-gray-900">
                Validate Your Startup Idea in 60 Seconds — For Free
              </h1>
              <p className="text-xl text-gray-700 mb-6">
                Get a demand score, competitor snapshot, key risks & real user sentiment — powered by live social conversations.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
                {/* <div className="flex items-center justify-center">
                  <svg className="h-5 w-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-gray-700">No signup required</span>
                </div> */}
                <div className="flex items-center justify-center">
                  <svg className="h-5 w-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-gray-700">Instant AI analysis</span>
                </div>
                <div className="flex items-center justify-center">
                  <svg className="h-5 w-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-gray-700"> Real-time market data</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Form & Sample */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left column: Input form */}
            <div>
              <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-6 py-5 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {isAnalyzing ? "Analyzing your idea..." : "Describe your startup idea"}
                  </h2>
                </div>
                <div className="p-6">
                  {!isAnalyzing ? (
                    <div className="space-y-6">
                      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-600">{error}</div>}
                      <div>
                        <label htmlFor="startup-idea" className="block text-sm font-medium text-gray-700 mb-1">
                          Your startup idea
                        </label>
                        <textarea
                          id="startup-idea"
                          value={idea}
                          onChange={(e) => setIdea(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                          rows="5"
                          placeholder="Describe your idea in a few sentences. For example: A nutrition app that connects users with personal coaches for daily meal guidance and accountability."
                          disabled={isAnalyzing}
                        />
                      </div>
                      <div>
                        <label htmlFor="industry-select" className="block text-sm font-medium text-gray-700 mb-1">
                          Industry (optional)
                        </label>
                        <select
                          id="industry-select"
                          value={industry}
                          onChange={(e) => setIndustry(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all appearance-none bg-white"
                          disabled={isAnalyzing}
                        >
                          <option value="">Select an industry</option>
                          <option value="tech">Technology/SaaS</option>
                          <option value="health">Health & Wellness</option>
                          <option value="finance">Finance/Fintech</option>
                          <option value="ecommerce">E-commerce</option>
                          <option value="education">Education</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <button
                        onClick={handleSubmit}
                        disabled={!idea.trim() || isAnalyzing}
                        className={`w-full px-5 py-3 rounded-xl flex items-center justify-center transition-all text-sm font-medium ${
                          !idea.trim()
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700 shadow-sm"
                        }`}
                        aria-label="Validate startup idea"
                      >
                        Validate My Idea <ChevronRight className="ml-1 h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-8">
                      <div>
                        <div className="flex justify-between text-sm text-gray-600 mb-2">
                          <span>Analyzing market data...</span>
                          <span>{Math.floor(analysisProgress)}%</span>
                        </div>
                        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-300 ease-out"
                            style={{ width: `${analysisProgress}%` }}
                            aria-label={`Analysis progress: ${Math.floor(analysisProgress)}%`}
                            role="progressbar"
                            aria-valuenow={Math.floor(analysisProgress)}
                            aria-valuemin="0"
                            aria-valuemax="100"
                          ></div>
                        </div>
                      </div>
                      <div className="space-y-3 text-sm text-gray-600">
                        <div className={`flex items-center ${analysisProgress > 20 ? "text-emerald-600" : "text-gray-400"}`}>
                          <Check className={`h-4 w-4 mr-2 ${analysisProgress > 20 ? "opacity-100" : "opacity-40"}`} />
                          <span>Scanning Reddit discussions</span>
                        </div>
                        <div className={`flex items-center ${analysisProgress > 40 ? "text-emerald-600" : "text-gray-400"}`}>
                          <Check className={`h-4 w-4 mr-2 ${analysisProgress > 40 ? "opacity-100" : "opacity-40"}`} />
                          <span>Analyzing market trends</span>
                        </div>
                        <div className={`flex items-center ${analysisProgress > 60 ? "text-emerald-600" : "text-gray-400"}`}>
                          <Check className={`h-4 w-4 mr-2 ${analysisProgress > 60 ? "opacity-100" : "opacity-40"}`} />
                          <span>Identifying competitors</span>
                        </div>
                        <div className={`flex items-center ${analysisProgress > 80 ? "text-emerald-600" : "text-gray-400"}`}>
                          <Check className={`h-4 w-4 mr-2 ${analysisProgress > 80 ? "opacity-100" : "opacity-40"}`} />
                          <span>Generating recommendations</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-8 bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Why Start Here?</h3>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-5 w-5 rounded-full bg-emerald-100 flex items-center justify-center mt-0.5">
                      <span className="text-emerald-600 text-xs">1</span>
                    </div>
                    <p className="ml-3 text-sm text-gray-600">
                      <span className="font-medium text-gray-900">Save time & money</span> by validating before building
                    </p>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-5 w-5 rounded-full bg-emerald-100 flex items-center justify-center mt-0.5">
                      <span className="text-emerald-600 text-xs">2</span>
                    </div>
                    <p className="ml-3 text-sm text-gray-600">
                      <span className="font-medium text-gray-900">Find your angle</span> that resonates with real users
                    </p>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-5 w-5 rounded-full bg-emerald-100 flex items-center justify-center mt-0.5">
                      <span className="text-emerald-600 text-xs">3</span>
                    </div>
                    <p className="ml-3 text-sm text-gray-600">
                      <span className="font-medium text-gray-900">Catch red flag</span> before they become costly mistakes
                    </p>
                  </div>
                </div>
              </div>

              {/* Premium Upsell Box */}
              {/* <div className="mt-8 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl shadow-sm p-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 flex items-center justify-center">
                      <Zap className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">🚀 Want a Full Business Plan + GTM Strategy?</h3>
                    <p className="text-sm text-gray-700 mb-3">Get a 100+ page investor-ready report tailored to your idea:</p>
                    <ul className="space-y-1.5 mb-4">
                      {[
                        "Market sizing (TAM/SAM/SOM)",
                        "Competitor matrix + positioning",
                        "Pricing suggestions",
                        "Customer personas & JTBD",
                        "Roadmap and GTM plan",
                        "PDF export included",
                      ].map((item, idx) => (
                        <li key={idx} className="flex items-start text-sm">
                          <svg
                            className="h-5 w-5 text-emerald-500 mr-1.5 flex-shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/dynamicreports/"
                      className="inline-flex items-center justify-center w-full px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-colors shadow-sm"
                    >
                      Try the Full Business Planner <ChevronRight className="ml-1 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div> */}
              <div className="mt-8 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl shadow-sm p-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 flex items-center justify-center">
                      <Zap className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">🔄 What Happens After Validation?</h3>
                    <p className="text-sm text-gray-700 mb-3">
                      FeedbackbyAI gives you more than just a score — it’s your full founder toolkit:
                    </p>
                    <ul className="space-y-1.5 mb-4 text-sm text-gray-800">
                      {[
                        "✅ 100+ Page Investor-Ready Business Plan",
                        "👥 Real-Time Buyer Intent Search",
                        "🎥 TikTok & Reels Generator (No filming needed)",
                        "💡 AI Brainstorming Tool for Idea Discovery",
                      ].map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <svg
                            className="h-5 w-5 text-emerald-500 mr-1.5 flex-shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/"
                      className="inline-flex items-center justify-center w-full px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-colors shadow-sm"
                    >
                      Explore the Full Platform <ChevronRight className="ml-1 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Right column: Sample report */}
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="px-6 py-5 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900">Sample Validation Report</h2>
                <span className="text-xs text-gray-500 px-3 py-1 bg-gray-100 rounded-full">Preview</span>
              </div>
              <div className="p-6">{renderSampleReport()}</div>
            </div>
          </div>
        </section>

        {/* How It Works Section - Added for SEO */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-gray-900">How Our Startup Idea Validator Works</h2>
            <p className="mt-2 text-gray-600 max-w-3xl mx-auto">
              Backed by real-time data, our AI analyzes live market signals to help you validate smarter — not guess harder.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                <Zap className="h-5 w-5 text-blue-700" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Real-Time Market Scanning</h3>
              <p className="text-gray-600">
                We tap into thousands of conversations across Reddit, founder forums, Twitter, and niche communities — revealing what your
                target audience actually wants right now.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center mb-4">
                <Users className="h-5 w-5 text-purple-700" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2"> Competitor Landscape Mapping</h3>
              <p className="text-gray-600">
                Instantly discover who's already building in your space, where they're strong or weak, and how you can stand out — before
                you write a single line of code.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="h-12 w-12 rounded-lg bg-emerald-100 flex items-center justify-center mb-4">
                <Lightbulb className="h-5 w-5 text-emerald-700" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Strategic Positioning & Risk Analysis</h3>
              <p className="text-gray-600">
                Get a clear recommendation on how to position your idea, what red flags to watch for, and where your opportunity really lies
                — all backed by data.
              </p>
            </div>
          </div>
        </section>

        {/* Complete Platform Banner - Add after the "How It Works" section */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
          <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-3xl overflow-hidden shadow-lg border border-gray-200">
            <div className="px-6 sm:px-10 py-8 sm:py-12">
              <div className="text-center mb-10">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Your Complete Founder Toolkit</h2>
                <p className="text-gray-700 max-w-2xl mx-auto">
                  Validation is just the beginning. Our all-in-one platform handles everything from idea to launch to growth.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {/* Business Plan */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mb-4">
                    <svg
                      className="h-6 w-6 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">100+ Page Business Plan</h3>
                  <p className="text-gray-600 text-sm mb-3">
                    Generate investor-ready reports with financial projections, competitor analysis, TAM/SAM/SOM & more.
                  </p>
                  <a
                    href="/dynamicreports/"
                    className="inline-flex items-center text-emerald-600 text-sm font-medium hover:text-emerald-700"
                  >
                    See Example <ArrowRight className="ml-1 h-3 w-3" />
                  </a>
                </div>

                {/* Lead Finder */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center mb-4">
                    <svg
                      className="h-6 w-6 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Buyer Intent Search</h3>
                  <p className="text-gray-600 text-sm mb-3">
                    Find people actively looking for solutions like yours across Reddit, Twitter, and LinkedIn. No cold outreach.
                  </p>
                  <a href="/leadfinder/" className="inline-flex items-center text-purple-600 text-sm font-medium hover:text-purple-700">
                    Find Leads <ArrowRight className="ml-1 h-3 w-3" />
                  </a>
                </div>

                {/* AI Content */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-pink-500 to-red-500 flex items-center justify-center mb-4">
                    <svg
                      className="h-6 w-6 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">AI Video Creator</h3>
                  <p className="text-gray-600 text-sm mb-3">
                    Create TikToks, Reels, and Shorts with 50+ AI avatars. No filming, no editing, just results that convert.
                  </p>
                  <a href="/video/" className="inline-flex items-center text-pink-600 text-sm font-medium hover:text-pink-700">
                    Generate Videos <ArrowRight className="ml-1 h-3 w-3" />
                  </a>
                </div>

                {/* Brainstorming */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mb-4">
                    <svg
                      className="h-6 w-6 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Idea Discovery</h3>
                  <p className="text-gray-600 text-sm mb-3">
                    Uncover untapped opportunities and spot trends before they become mainstream with our AI brainstorming tool.
                  </p>
                  <a href="/ideate/" className="inline-flex items-center text-blue-600 text-sm font-medium hover:text-blue-700">
                    Start Discovering <ArrowRight className="ml-1 h-3 w-3" />
                  </a>
                </div>
              </div>

              <div className="mt-10 text-center">
                <a
                  href="/"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white text-sm font-medium transition-all shadow-sm"
                >
                  Explore the Complete Platform <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </section>

        <div className="relative z-10 w-full max-w-6xl mx-auto text-center mt-20 sm:mt-24 lg:mt-32">
          {/* Trust Badge */}
          <motion.div
            variants={delayedFadeIn}
            initial="hidden"
            animate="visible"
            className="inline-flex items-center gap-2 border border-gray-200 bg-white/70 backdrop-blur-sm rounded-full px-4 py-1.5 text-sm text-gray-600"
          >
            <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
            Trusted by 10,000+ businesses
          </motion.div>

          {/* Testimonials Section */}
          <motion.div variants={delayedFadeIn} initial="hidden" animate="visible" className="mt-12 sm:mt-16 max-w-5xl mx-auto">
            <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
              <IframeResizer
                src="https://embed-v2.testimonial.to/w/feedbackbyai"
                style={{ minHeight: "450px", width: "100%" }}
                className="w-full"
                scrolling="no"
                frameBorder="0"
              />
            </div>
          </motion.div>
        </div>

        {/* FAQ Section - Added for SEO */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-16 bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200">
          <div className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>

            <div className="space-y-6 max-w-3xl mx-auto">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">How accurate is the startup idea validator?</h3>
                <p className="text-gray-600">
                  Our validator analyzes real-time market data from multiple sources, providing insights based on actual discussions and
                  trends rather than assumptions. It has helped thousands of founders identify viable startup opportunities with a high
                  degree of accuracy.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Can I validate any type of startup idea?</h3>
                <p className="text-gray-600">
                  Yes, our tool can validate ideas across multiple industries including technology, health, finance, education, e-commerce,
                  and more. Simply provide a description of your startup concept and select the appropriate industry.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">How is the demand score calculated?</h3>
                <p className="text-gray-600">
                  The demand score is calculated using a proprietary algorithm that analyzes market signals, user sentiment, competitive
                  landscape, and growth potential. A higher score indicates stronger market demand for your idea.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">What do I get in the free validation report?</h3>
                <p className="text-gray-600">
                  The free report includes a demand score, key market signals, top competitors, potential risks, a recommended positioning
                  angle, and user sentiment analysis. For more comprehensive analysis, consider upgrading to our full business planner.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Call to action */}
        <section id="cta" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <div className="bg-green-900 rounded-2xl shadow-xl p-8 sm:p-12 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Ready to Validate Yours — in Just 60 Seconds?</h2>
            <p className="text-gray-300 max-w-2xl mx-auto mb-8">
              Don't waste months building something nobody wants. Validate your idea in 60 seconds with real market data.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="#top"
                className="px-6 py-3 bg-white text-gray-900 font-medium rounded-lg hover:bg-gray-100 transition-colors shadow-sm"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              >
                Validate for Free
              </a>
              <Link
                to="/dynamicreports/"
                className="px-6 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
              >
                Get Full Business Plan
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <EnhancedFooter />
    </div>
  );
};

export default FreeIdeaValidator;
